import {SyntheticEvent, useCallback, useEffect, useMemo, useState} from 'react';
import './style.scss';
import {useLocation, useParams, Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../i18n/translations';
import {Stack, SxProps, Typography, useTheme} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CollapsibleShiftInfo from '../../../../components/collapsible-shift-info';
import {TabPanel, Tabs, Tab} from '../../../../components/shared/tabs';
import {FullPageContainer} from '../../../../components/shared/page-wrapper';
import {appSizes} from '../../../../utils/constants';
import {SimpleShiftTabView} from './tabs/simple';
import {GroupedShiftTabView} from './tabs/grouped';
import {UsersShiftTabView} from './tabs/users';
import {EntityShift} from '../../../../backend/interfaces/entity-shift';
import {CareTaskComplementary} from '../../../../backend/interfaces/care-task-complementary';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import {EntityShiftsRepository} from '../../../../repositories/entity-shifts';
import {useDailyCareLog} from '../../../../contexts/daily-care-log/context';
import {useEntity} from '../../../../contexts/entity/context';
import {useShiftDetailsSearchParams} from './use-shift-search-params';
import {EntityUserCareTasksRepository} from '../../../../repositories/entity-user-care-tasks';
import {CareTask} from '../../../../backend/interfaces/care-task';
import {EntityCareServiceRepository} from '../../../../repositories/entity-care-services';
import moment from 'moment';

type LocationState = {
  shift: EntityShift;
};

const checkIconSx: SxProps = {
  fontSize: '20px',
  color: 'var(--dcm-green-base)',
};

const allTasksHandledTextSx: SxProps = {
  textAlign: 'center',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: 'normal',
  color: 'var(--dcm-gray)',
  maxWidth: {xs: '188px', sm: 'unset'},
};

const TABS = ['simple', 'grouped', 'user'];

type ShiftDetailsCareLogScreenProps = {
  users?: AffiliatedUser[];
};

export default function ShiftDetailsCareLogScreen({users}: ShiftDetailsCareLogScreenProps) {
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [shift, setShift] = useState<EntityShift | undefined>(undefined);
  const [shiftCareTasks, setShiftCareTasks] = useState<CareTaskComplementary[] | undefined>(
    undefined
  );
  let {entityId, entityShiftId}: {entityId: string; entityShiftId: string} = useParams();
  const {t} = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const dailyCareLogContext = useDailyCareLog();
  const {state: entityState} = useEntity();
  const {date, endDate, startDate, careServiceUserId} = useShiftDetailsSearchParams();

  const handleTabChange = (_event: SyntheticEvent, newValue: number) =>
    setCurrentTabIndex(newValue);

  const fetchShift = useCallback(async () => {
    if (careServiceUserId) {
      // --- Care Service HACK ---
      // For care services, we don't have entity shifts, so we create a fake one.
      EntityCareServiceRepository.instance
        .getCareService(entityId, entityShiftId)
        .then((careService) => {
          const fakeEntityShift: EntityShift = {
            id: careService.id,
            name: careService.title ?? '',
            abbreviation: '',
            startHour: moment(careService.startsAt).toDate(),
            endHour: moment(careService.endsAt).toDate(),
            backgroundColor: careService.foregroundColor ?? '#fff',
            entityId: entityId,
            responsibleUserId: undefined,
            foregroundColor: '#FFFFFF',
            // @ts-ignore
            userId: careService.userId,
          };
          setShift(fakeEntityShift);
        })
        .catch((error) => console.error(`Erro: ${error}`));
      return;
    }

    EntityShiftsRepository.instance
      .getShift(entityId, entityShiftId)
      .then((shift) => setShift(shift))
      // hack: try again
      .catch(() => {})
      .catch((error) => console.error(`Erro: ${error}`));
  }, [entityId, entityShiftId, careServiceUserId]);

  const fetchShiftCareTasks = useCallback(async () => {
    try {
      let caretasks: CareTaskComplementary[] | CareTask[];
      // --- Care Service HACK ---
      if (careServiceUserId) {
        caretasks = await EntityUserCareTasksRepository.instance
          .GetCareTasksWithComplementaryInformation(
            entityId,
            careServiceUserId,
            startDate.utc().toISOString(),
            endDate.utc().toISOString()
          )
          .then((response) => response.data);
      } else {
        caretasks = await EntityShiftsRepository.instance.getShiftCareTasks(
          entityId,
          entityShiftId,
          startDate.utc().toISOString(),
          endDate.utc().toISOString(),
          true,
          {
            headers: {
              'cache-control': 'no-cache',
            },
          }
        );
      }

      // ##############################################################
      // HACK: Backend should return care tasks from active users only
      // TODO: Remove this hack when backend is fixed
      // ##############################################################
      const filterCareTasks = (caretasks as CareTaskComplementary[])?.filter((task) =>
        users?.find((u) => u.user?.id === task.careTask.userId)
      );

      setShiftCareTasks(filterCareTasks);
    } catch (error) {
      console.error(`🔴 Erro: ${error}`);
    }
  }, [careServiceUserId, entityId, startDate, endDate, entityShiftId, users]);

  const seniorsMap: Map<string, AffiliatedUser> = useMemo(() => {
    const map = new Map<string, AffiliatedUser>();

    users?.forEach((senior) => map.set(senior.user!.id!, senior));

    return map;
  }, [users]);

  const allTasksHandled =
    Array.isArray(shiftCareTasks) &&
    shiftCareTasks?.length > 0 &&
    shiftCareTasks?.every((task) => task.careTask.markedAsHandled);

  useEffect(() => {
    if (location.state) {
      setShift((location.state as LocationState).shift);
    } else {
      fetchShift();
    }
  }, [location.state, fetchShift]);

  useEffect(() => {
    if (shift) fetchShiftCareTasks();
  }, [fetchShiftCareTasks, shift]);

  useEffect(() => {
    const tabFromURL: string | undefined = location.hash?.replace('#', '');

    if (tabFromURL) {
      const tabIndex = TABS.findIndex((value) => value === tabFromURL);
      setCurrentTabIndex(tabIndex < 0 ? 0 : tabIndex);
    }
  }, [location.hash, location.search]);

  useEffect(() => {
    dailyCareLogContext.setNavigationHeader(false);
    dailyCareLogContext.toggleBottomNavigation(true);

    return () => {
      dailyCareLogContext.setNavigationHeader(false);
      dailyCareLogContext.toggleBottomNavigation(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack className="shift-details">
      <FullPageContainer>
        {shift && (
          <CollapsibleShiftInfo shift={shift} date={date} startDate={startDate} endDate={endDate} />
        )}
      </FullPageContainer>
      <FullPageContainer
        sx={{
          position: 'sticky',
          top: `${appSizes.dailyCareLog.headerHeight}px`,
          backgroundColor: theme.palette.background.default,
          zIndex: 1,
        }}
      >
        <Tabs value={currentTabIndex} onChange={handleTabChange} aria-label="Shift care-task tabs">
          {TABS.map((tab, index) => (
            <Tab
              key={`tab-${tab}`}
              label={t(
                translations.daily_care_log.shift_details.tabs[tab as 'simple' | 'grouped' | 'user']
                  .title
              )}
              value={index}
              component={Link}
              to={`${location.search}#${TABS[index]}`}
            />
          ))}
        </Tabs>
      </FullPageContainer>

      {allTasksHandled && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing="8px"
          paddingX="10px"
        >
          <CheckIcon sx={checkIconSx} />
          <Typography sx={allTasksHandledTextSx}>
            {t(translations.daily_care_log.shift_details.all_tasks_handled)}
          </Typography>
        </Stack>
      )}

      <TabPanel className="simple-tab" value={currentTabIndex} index={0}>
        <SimpleShiftTabView
          tasks={shiftCareTasks}
          seniors={seniorsMap}
          entitySocialResponses={entityState.availableSocialResponses}
        />
      </TabPanel>
      <TabPanel className="grouped-tab" value={currentTabIndex} index={1}>
        <GroupedShiftTabView tasks={shiftCareTasks} seniors={seniorsMap} />
      </TabPanel>
      <TabPanel className="utents-tab" value={currentTabIndex} index={2}>
        <UsersShiftTabView tasks={shiftCareTasks} seniors={seniorsMap} />
      </TabPanel>
    </Stack>
  );
}
